// The "Buscar" destination — message search across the team (shared/API.md §23), the web twin of
// ios/Sources/Views/MessageSearchView.swift. Contract: `query` is the URL's ?q= (may be empty);
// committing a query calls useShell().openSearch(q) so it lands in the URL.
//
// Two passes (docs/PERFORMANCE.md): the LOCAL FTS index answers every keystroke — never waiting on
// the network, measured as `[satori-perf] localSearch` — and the SERVER runs on Enter and on a
// pause, replacing the list when it lands (searchModel.ts holds the rules about when it may not).
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { data } from '../../../bridge/client';
import * as SearchQuery from '../../../core/searchQuery';
import type { SearchBundle } from '../../../data/features/search';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { useMinuteClock } from '../../hooks';
import { DestinationFrame, EmptyState, InlineStatus, SearchInput } from '../../kit/Kit';
import { Highlighted, PostResultRow } from '../../kit/PostResultRow';
import { record } from '../../perf';
import { useShell } from '../../shell/ShellContext';
import { buildRows, initialState, insertModifier, isoDay, phase, reduce, timeZoneOffsetSeconds } from './searchModel';
import type { Row } from '../../../core/searchResultsPresentation';
import type { ChannelRecord, PostRecord, UserRecord } from '../../../core/records';
import './search.css';

/** Quiet time after the last keystroke before the server is asked (Swift: local pass + 300 ms). */
const SERVER_PAUSE_MS = 450;

export function SearchView({ query }: { query: string }) {
  const shell = useShell();
  const shellRef = useRef(shell);
  shellRef.current = shell;
  const teamId = shell.team.id;
  const [text, setText] = useState(query);
  const [state, dispatch] = useReducer(reduce, query, initialState);
  const [recents, setRecents] = useState<string[]>([]);
  const now = useMinuteClock();
  const fieldRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const textRef = useRef(text);
  textRef.current = text;
  /** The query last pushed to / received from the URL — a prop equal to it is our own echo. */
  const committed = useRef<string | null>(null);
  const serverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);
  const pendingSelection = useRef<[number, number] | null>(null);

  // MARK: - The local pass: in flight at most once, the newest text queued behind it (no fixed
  // debounce — the index answers in a few ms, and a keystroke never waits for a timer).
  const local = useRef<{ running: boolean; queued: { text: string; at: number } | null; perf: { at: number; q: string; hits: number; workerMs: number; rttMs: number } | null }>({
    running: false,
    queued: null,
    perf: null,
  });
  const [localStamp, setLocalStamp] = useState(0);

  const runLocal = useCallback(
    (raw: string, at: number) => {
      const l = local.current;
      if (l.running) {
        l.queued = { text: raw, at };
        return;
      }
      l.running = true;
      const sent = performance.now();
      data
        .call('searchMessagesLocal', teamId, raw)
        .then((bundle: SearchBundle) => {
          if (raw.trim() === textRef.current.trim()) {
            l.perf = { at, q: raw.trim(), hits: bundle.posts.length, workerMs: bundle.readMs, rttMs: performance.now() - sent };
            dispatch({ type: 'local', query: raw, bundle });
            setLocalStamp((n) => n + 1);
          }
        })
        .catch((error) => console.warn('[satori] local search failed', error))
        .finally(() => {
          l.running = false;
          const next = l.queued;
          l.queued = null;
          if (next) runLocal(next.text, next.at);
        });
    },
    [teamId],
  );

  // Measured from the keystroke to the frame after the rows were committed (docs/PERFORMANCE.md:
  // local search → first results on screen < 50 ms).
  useLayoutEffect(() => {
    const p = local.current.perf;
    if (!p) return;
    local.current.perf = null;
    requestAnimationFrame(() => record('localSearch', performance.now() - p.at, `${p.hits} hits · worker ${p.workerMs.toFixed(1)}ms · round trip ${p.rttMs.toFixed(0)}ms · "${p.q}"`));
  }, [localStamp]);

  // Names follow the store: an author or channel resolved (or renamed) after the answer was read
  // replaces "Alguien" / "Canal" in place, without asking anyone again.
  useEffect(() => {
    let scheduled = false;
    return data.onChanges((tables) => {
      if (!(tables.has('users') || tables.has('channels') || tables.has('channelParticipants'))) return;
      if (scheduled || !stateRef.current.posts.length) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const ids = stateRef.current.posts.map((p) => p.id);
        if (!ids.length) return;
        data.call('searchBundleFor', ids).then((bundle) => dispatch({ type: 'names', bundle }), () => undefined);
      });
    });
  }, []);

  // MARK: - The server pass

  const runServer = useCallback(
    (raw: string, nextPage: boolean, rememberIt = false) => {
      const q = raw.trim();
      if (q === '') return;
      if (serverTimer.current) clearTimeout(serverTimer.current);
      serverTimer.current = null;
      const s = stateRef.current;
      if (nextPage && (s.serverRequest !== 0 || !s.hasMore)) return;
      const request = ++requestSeq.current;
      const page = nextPage ? s.page + 1 : 0;
      dispatch({ type: 'serverStart', query: q, request, nextPage });
      if (rememberIt) remembered.current = q;
      data
        .call('searchMessagesServer', teamId, q, page, timeZoneOffsetSeconds(), rememberIt)
        .then((outcome) => {
          if (outcome.recents) setRecents(outcome.recents);
          dispatch({ type: 'serverDone', query: q, request, nextPage, outcome });
        })
        .catch(() => dispatch({ type: 'serverDone', query: q, request, nextPage, outcome: { kind: 'failed', reason: 'network' } }));
    },
    [teamId],
  );

  /** The query last put on top of the recents (a submit does it inside its server call). */
  const remembered = useRef('');
  const remember = useCallback(
    (raw: string) => {
      const q = raw.trim();
      if (q === '' || q === remembered.current) return;
      remembered.current = q;
      data.call('rememberSearch', teamId, q).then(setRecents, () => undefined);
    },
    [teamId],
  );

  /** Typed (or set): local now, server after a pause. */
  const change = useCallback(
    (value: string, at = performance.now()) => {
      setText(value);
      textRef.current = value;
      dispatch({ type: 'typed', query: value });
      if (serverTimer.current) clearTimeout(serverTimer.current);
      serverTimer.current = null;
      if (value.trim() === '') {
        if (committed.current) {
          committed.current = '';
          shellRef.current.openSearch('');
        }
        return;
      }
      runLocal(value, at);
      serverTimer.current = setTimeout(() => {
        runServer(value, false);
        // The URL follows the query the server was asked (the shell REPLACES the entry while the
        // search is open, so a pause is not a history step) — a reload lands on the same results.
        const q = value.trim();
        if (committed.current !== q) {
          committed.current = q;
          shellRef.current.openSearch(q);
        }
      }, SERVER_PAUSE_MS);
    },
    [runLocal, runServer],
  );

  /** Enter / a recent / a query from the URL: remembered, in the URL, and asked of the server NOW. */
  const submit = useCallback(
    (value: string, fromUrl = false) => {
      const q = value.trim();
      if (value !== textRef.current || stateRef.current.query !== q) change(value);
      if (q === '') return;
      runServer(value, false, true);
      if (!fromUrl && committed.current !== q) {
        committed.current = q;
        shell.openSearch(q);
      }
    },
    [change, runServer, shell],
  );

  // The URL's query (first mount, the header's search box, back/forward) is a submitted search.
  useEffect(() => {
    if (committed.current === query.trim()) return;
    committed.current = query.trim();
    if (query.trim() === '') {
      if (textRef.current.trim() !== '') change('');
      return;
    }
    submit(query, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, teamId]);

  useEffect(() => {
    let live = true;
    data.call('recentSearches', teamId).then((list) => live && setRecents(list), () => undefined);
    return () => {
      live = false;
    };
  }, [teamId]);

  useEffect(
    () => () => {
      if (serverTimer.current) clearTimeout(serverTimer.current);
    },
    [],
  );

  // A chip's inserted date is selected once the controlled input holds the new text.
  useEffect(() => {
    const sel = pendingSelection.current;
    if (!sel) return;
    pendingSelection.current = null;
    const input = fieldRef.current?.querySelector('input');
    if (!input) return;
    input.focus();
    input.setSelectionRange(sel[0], sel[1]);
  }, [text]);

  const insertChip = (key: string) => {
    const next = insertModifier(text, key, isoDay(Date.now()));
    pendingSelection.current = next.selection;
    change(next.text);
  };

  const forget = (entry: string) => data.call('forgetSearch', teamId, entry).then(setRecents, () => undefined);
  const clearAll = () => data.call('clearRecentSearches', teamId).then(setRecents, () => undefined);

  const open = (postId: string) => {
    const q = textRef.current.trim();
    if (q) {
      // Later, not now: the store write would sit in front of the jump's own reads.
      setTimeout(() => remember(q), 1500);
      // Back from the message lands on THESE results.
      if (committed.current !== q) {
        committed.current = q;
        shell.openSearch(q);
      }
    }
    shell.jumpToPost(postId);
  };

  // Rows depend on the ANSWER, not on the text: a keystroke alone re-renders nothing below the field.
  const { posts, postsFor, usersById, channelsById, participants, currentUserId } = state;
  const openLatest = useRef(open);
  openLatest.current = open;
  const openRef = useCallback((postId: string) => openLatest.current(postId), []);

  const rows = useMemo(
    () => buildRows({ posts, postsFor, query: postsFor, usersById, channelsById, participants, currentUserId }, now),
    [posts, postsFor, usersById, channelsById, participants, currentUserId, now],
  );
  const current = phase(state, text);

  // Infinite scroll: the sentinel under the last row asks for the next server page.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !state.hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) runServer(textRef.current, true);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [state.hasMore, state.posts.length, runServer]);

  let body: ReactNode;
  switch (current) {
    case 'recents':
      body = recents.length ? (
        <div className="group search-recents">
          <div className="group-title search-recents-title">
            <span>{t('search.recents')}</span>
            <button className="link-button" onClick={() => void clearAll()}>
              {t('web.search.clearRecents')}
            </button>
          </div>
          <div className="group-rows">
            {recents.map((entry) => (
              <div key={entry} className="list-row clickable search-recent" role="button" tabIndex={0} onClick={() => submit(entry)} onKeyDown={(e) => e.key === 'Enter' && submit(entry)}>
                <span className="list-row-leading">
                  <Icon name="history" size={16} />
                </span>
                <span className="list-row-text">
                  <span className="list-row-title">{entry}</span>
                </span>
                <button
                  className="icon-button search-recent-remove"
                  title={t('web.search.removeRecent')}
                  aria-label={t('web.search.removeRecent')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void forget(entry);
                  }}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState title={t('web.search.start.title')} body={t('web.search.start.body')} />
      );
      break;
    case 'searching':
      body = <InlineStatus kind="loading" text={t('web.search.searchingServer')} />;
      break;
    case 'failed':
      // "Could not search" is a different sentence from "nothing matched": one is worth retrying.
      body = (
        <EmptyState
          title={t('search.failed.title')}
          body={t('search.failed.description')}
          action={
            <button className="btn btn-secondary" onClick={() => runServer(text, false)}>
              <Icon name="retry" size={15} />
              {t('common.retry')}
            </button>
          }
        />
      );
      break;
    case 'typedNotSubmitted':
      // The index only holds what this device cached: an empty pane here means "ask the server",
      // and has to SAY so rather than read as "nothing exists".
      body = <p className="search-hint">{t('search.submitHint')}</p>;
      break;
    case 'empty':
      body = <EmptyState title={t('search.empty.title')} body={t('search.empty.description')} />;
      break;
    case 'results':
      body = (
        <>
          {state.cachedOnly || state.failed ? (
            <InlineStatus kind={state.failed ? 'error' : 'loading'} text={t('search.cachedOnly')} onRetry={state.failed ? () => runServer(text, false) : undefined} />
          ) : null}
          <ResultsList rows={rows} posts={posts} usersById={usersById} channelsById={channelsById} busy={state.serverRequest !== 0} onOpen={openRef} />
          {state.hasMore ? (
            <div ref={sentinel} className="search-more">
              {state.loadingMore ? (
                <InlineStatus kind="loading" text={t('web.search.searchingServer')} />
              ) : (
                <button className="btn btn-quiet" onClick={() => runServer(text, true)}>
                  {t('web.search.more')}
                </button>
              )}
            </div>
          ) : null}
          {state.serverRequest !== 0 && !state.loadingMore ? <InlineStatus kind="loading" text={t('web.search.searchingServer')} /> : null}
        </>
      );
      break;
  }

  const toolbar = (
    <div className="search-toolbar" ref={fieldRef}>
      <div
        className="search-field-row"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && text !== '') {
            e.stopPropagation();
            change('');
          }
        }}
      >
        <SearchInput value={text} onChange={(v) => change(v)} placeholder={t('search.placeholder')} autoFocus onEnter={() => submit(textRef.current)} />
      </div>
      <div className="search-modifiers" aria-label={t('web.search.modifiersLabel')}>
        {SearchQuery.modifierKeys.map((key) => (
          <button key={key} className="search-modifier" title={t(`web.search.modifier.${key}.hint`)} onClick={() => insertChip(key)}>
            <code>{key}:</code>
            <span>{t(`web.search.modifier.${key}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <DestinationFrame icon="search" title={t('search.title')} subtitle={shell.team.displayName} toolbar={toolbar}>
      <div className="search-body">{body}</div>
    </DestinationFrame>
  );
}

/** The hits. Memoised: it re-renders when the ANSWER changes, never per keystroke. */
const ResultsList = memo(function ResultsList({
  rows,
  posts,
  usersById,
  channelsById,
  busy,
  onOpen,
}: {
  rows: Row[];
  posts: PostRecord[];
  usersById: Record<string, UserRecord>;
  channelsById: Record<string, ChannelRecord>;
  busy: boolean;
  onOpen(postId: string): void;
}) {
  const postsById = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);
  return (
    <div className="search-results" role="list" aria-busy={busy}>
      {rows.map((row) => {
        const post = postsById.get(row.postId);
        const channel = channelsById[row.channelId];
        return (
          <div role="listitem" key={row.postId}>
            <PostResultRow
              userId={post?.userId ?? ''}
              authorName={row.authorName}
              authorVersion={usersById[post?.userId ?? '']?.lastPictureUpdate ?? 0}
              place={row.channelName}
              placeIsPrivate={channel?.type === 'P'}
              placeIcon={channel?.type === 'D' || channel?.type === 'G' ? 'at' : undefined}
              time={row.timeLabel}
              snippet={<Highlighted text={row.message} ranges={row.highlights} />}
              badge={
                post?.rootId ? (
                  <span className="search-badge">
                    <Icon name="reply" size={11} />
                    {t('web.search.inThread')}
                  </span>
                ) : undefined
              }
              onOpen={() => onOpen(row.postId)}
            />
          </div>
        );
      })}
    </div>
  );
});
